import { MeshBuilder, Vector3 } from "@babylonjs/core";
import { HeroId, MoveKind, Slot, fxk, hv } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { brass, click, fm, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero } from "../types.ts";

const V = { corrosionNet: 0, corrosionTicks: 1, corrosionFull: 2, burrowed: 3 } as const;
const FX_DIVE = fxk(HeroId.Venison, 1);
const FX_MAW = fxk(HeroId.Venison, 2);
const cue = (db: number, f: Cue) => trim(db, f);
const tendrilStrike = cue(16, (o) => {
  click(o, 0.16, 1200, 0.005);
  for (let i = 0; i < 4; i++) {
    whoosh({ ...o, t: o.t + i * 0.018 }, 0.18, 240 + i * 90, 1300 + i * 300, 0.11);
    fm({ ...o, t: o.t + i * 0.018 }, { f: 95 + i * 14, ratio: 2.2, index: 3.2, index1: 0.4, gain: 0.055, d: 0.16 });
  }
  noise(o, { color: "crackle", type: "bandpass", f: 900, gain: 0.12, d: 0.12 });
  return 0.28;
});
const corrosion = cue(10, (o) => {
  fm(o, { f: 120, ratio: 2.6, index: 5, gain: 0.14, d: 0.35 });
  noise(o, { color: "brown", type: "bandpass", f: 500, gain: 0.35, d: 0.45 });
  return 0.6;
});
const resilience = cue(0, (o) => {
  thump(o, 0.8, 70, 35, 0.3);
  tone(o, { type: "sawtooth", f: 92, f1: 160, glide: 0.25, gain: 0.08, d: 0.45, lp: 800 });
  return 0.7;
});
const dive = cue(4, (o) => {
  whoosh(o, 0.65, 300, 1700, 0.35);
  thump(o, 0.5, 90, 38, 0.18);
  return 0.5;
});
const maw = cue(-4, (o) => {
  thump(o, 1, 48, 22, 0.7);
  noise(o, { color: "brown", type: "lowpass", f: 700, gain: 0.65, d: 1 });
  return 1.3;
});

const theme = {
  bpm: 82,
  octave: -1,
  voice: brass,
  notes: [
    [0, 0.75],
    [2, 0.25],
    [7, 0.75],
    [6, 0.25],
    [-5, 1.25],
  ],
} satisfies ClientHero["audio"]["theme"];

const hero: ClientHero = {
  id: HeroId.Venison,
  bulk: 1.38,
  shape: { head: 1.12, torso: 1.18, arms: 1.25, legs: 1.05 },
  gear(g) {
    const { scene, add, addWorld, rig, glow, toon, dark, R, H } = g;
    for (const side of [-1, 1]) {
      const antler = add(MeshBuilder.CreateCylinder("velvet-antler", { diameterTop: 0.04, diameterBottom: 0.12, height: 0.78, tessellation: 8 }, scene), rig.head, toon("#d2b48c"));
      antler.position.set(side * R * 0.65, R * 0.9, -R * 0.12);
      antler.rotation.z = side * 0.45;
      const tine = add(MeshBuilder.CreateCylinder("velvet-tine", { diameterTop: 0.03, diameterBottom: 0.075, height: 0.38, tessellation: 8 }, scene), rig.head, toon("#d2b48c"));
      tine.position.set(side * R * 0.82, R * 1.12, -R * 0.24);
      tine.rotation.z = side * 1.0;
    }
    for (const side of [-1, 1]) {
      const eye = add(MeshBuilder.CreateSphere("venison-white-eye", { diameter: R * 0.55, segments: 10 }, scene), rig.head, glow("#f5f1df"));
      eye.position.set(side * R * 0.36, R * 0.08, -R * 0.82);
      eye.scaling.set(1.55, 0.42, 0.08);
      eye.rotation.z = -side * 0.18;
    }
    for (let i = 0; i < 7; i++) {
      const tooth = add(MeshBuilder.CreateBox("venison-tooth", { width: 0.045, height: 0.18, depth: 0.035 }, scene), rig.head, toon("#f5f1df"));
      tooth.position.set((i - 3) * R * 0.14, -R * 0.33 - (i % 2) * 0.04, -R * 0.86);
      tooth.rotation.z = (i - 3) * 0.08;
    }
    const back = add(MeshBuilder.CreateBox("venison-back-mass", { width: R * 1.6, height: H * 0.38, depth: 0.25 }, scene), rig.torso, dark);
    back.position.set(0, 0.05, 0.34);
    const chestMark = add(MeshBuilder.CreateTorus("venison-chest-mark", { diameter: R * 1.25, thickness: 0.045, tessellation: 28 }, scene), rig.torso, glow("#f5f1df", 0.82));
    chestMark.position.set(0, 0.08, -0.38);
    chestMark.scaling.y = 1.45;
    chestMark.rotation.x = Math.PI / 2;
    for (const side of [-1, 1]) {
      const tendril = add(MeshBuilder.CreateCylinder("venison-tendril", { diameter: 0.08, height: 0.9, tessellation: 8 }, scene), side < 0 ? rig.handL : rig.handR, glow("#2c2d3f"));
      tendril.rotation.x = Math.PI / 2;
      tendril.position.z = -0.48;
      for (let i = 0; i < 3; i++) {
        const lash = add(MeshBuilder.CreateCylinder("venison-finger-lash", { diameter: 0.035, height: 0.52 + i * 0.08, tessellation: 6 }, scene), side < 0 ? rig.handL : rig.handR, glow("#050609", 0.92));
        lash.rotation.x = Math.PI / 2 + (i - 1) * 0.08;
        lash.rotation.z = side * (0.2 + i * 0.08);
        lash.position.set((i - 1) * 0.055, -0.02, -0.45 - i * 0.04);
      }
    }
    const tether = addWorld(MeshBuilder.CreateCylinder("venison-tether", { diameter: 0.06, height: 1, tessellation: 6 }, scene), glow("#5a5a82", 0.8));
    tether.isVisible = false;
    const swingLine = addWorld(MeshBuilder.CreateCylinder("venison-swing-tether", { diameter: 0.075, height: 1, tessellation: 8 }, scene), glow("#050609", 0.85));
    swingLine.isVisible = false;
    return { tether, back, chestMark, swingLine };
  },
  animate(gear, f) {
    const g = gear as { tether: ReturnType<typeof MeshBuilder.CreateCylinder>; back: ReturnType<typeof MeshBuilder.CreateBox>; chestMark: ReturnType<typeof MeshBuilder.CreateTorus>; swingLine: ReturnType<typeof MeshBuilder.CreateCylinder> };
    const target = f.v[V.corrosionNet] ? f.lookup(Math.round(f.v[V.corrosionNet]!)) : undefined;
    g.tether.isVisible = !!target?.alive && f.v[V.corrosionTicks] > 0;
    if (target) {
      const a = new Vector3(f.eye.x, f.eye.y - 0.25, f.eye.z);
      const b = new Vector3(target.center.x, target.center.y, target.center.z);
      const len = Vector3.Distance(a, b);
      g.tether.position = Vector3.Lerp(a, b, 0.5);
      g.tether.scaling.y = len;
      g.tether.lookAt(b);
      g.tether.rotation.x += Math.PI / 2;
    }
    g.back.scaling.setAll(1 + (f.v[V.burrowed] ? 0.12 : 0) + Math.sin(f.t * 5) * 0.02);
    g.chestMark.scaling.set(1 + Math.sin(f.t * 6) * 0.035, 1.45 + Math.sin(f.t * 5) * 0.04, 1);
    g.swingLine.isVisible = f.moveKind === MoveKind.Swing;
    if (g.swingLine.isVisible) {
      const a = new Vector3(f.eye.x, f.eye.y - 0.35, f.eye.z);
      const b = new Vector3(f.eye.x + f.aim.x * 18, f.eye.y + Math.max(5, f.aim.y * 18 + 6), f.eye.z + f.aim.z * 18);
      const len = Vector3.Distance(a, b);
      g.swingLine.position = Vector3.Lerp(a, b, 0.5);
      g.swingLine.scaling.y = len;
      g.swingLine.lookAt(b);
      g.swingLine.rotation.x += Math.PI / 2;
    }
    if (f.v[V.burrowed] === 1) return "drive";
    if (f.moveKind === MoveKind.Swing) return "holding-both";
    if (f.moveKind >= MoveKind.Hero) return "attack-kick-right";
  },
  hud(c, eid) {
    return [
      { label: "Corrosion", value: hv(c, eid, V.corrosionTicks) / 180, color: "#5a5a82" },
      { label: "Burrow", value: hv(c, eid, V.burrowed) ? 1 : 0, color: "#d2b48c" },
    ];
  },
  fx: {
    [FX_DIVE]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("venison-impact", { diameter: radius * 2, thickness: 0.18, tessellation: 36 }, c.scene);
        ring.material = c.glow("#d2b48c", 0.55);
        ring.position.set(pos.x, pos.y + 0.05, pos.z);
        c.timed(ring, 0.45, (t, m) => (m.scaling.setAll(0.4 + t)));
      },
    },
    [FX_MAW]: {
      explosion(c, pos, radius) {
        const dome = MeshBuilder.CreateSphere("venison-maw", { diameter: radius * 2, segments: 16, slice: 0.5 }, c.scene);
        dome.material = c.glow("#2c2d3f", 0.45);
        dome.position.set(pos.x, pos.y, pos.z);
        c.timed(dome, 0.6, (t, m) => {
          m.scaling.y = 0.25 + t * 0.8;
          m.scaling.x = m.scaling.z = 0.2 + t;
        });
      },
    },
  },
  audio: {
    weight: "heavy",
    fire: () => ({ key: "fire:venison:tendrils", cue: tendrilStrike }),
    ability: { [Slot.Secondary]: corrosion, [Slot.Ability1]: dive, [Slot.Ability2]: resilience, [Slot.Ability3]: dive, [Slot.Ultimate]: maw },
    theme,
    themeTrim: { heroic: -2, hostile: -7, tease: -2.5 },
    loops: ({ v, alive }) => (alive && v[V.burrowed] === 2 ? [{ key: "wall-crawl", build: loops.climb, gain: 0.75 }] : []),
  },
  icons: {
    [Slot.Primary]: ["M4 8c5 0 5 8 10 8", "M4 12c6-2 8 5 16 4", "M4 16c5-4 8 1 14-5"],
    [Slot.Secondary]: ["M5 6c6 1 12 5 14 12", "M5 18c3-4 8-5 14-2", "M7 10l10 4"],
    [Slot.Ability1]: ["M4 20c5-10 10-14 16-16", "M8 12c4-1 7 2 8 7"],
    [Slot.Ability2]: ["M12 3l7 5v5c0 4-3 7-7 8-4-1-7-4-7-8V8z", "M9 12h6M12 9v6"],
    [Slot.Ability3]: ["M12 4v13", "M6 11l6 6 6-6", "M4 20h16"],
    [Slot.Ultimate]: ["M4 15c3-8 13-8 16 0", "M7 15c2 4 8 4 10 0", "M9 12l1 3M14 12l-1 3"],
    passive: ["M6 20V8", "M18 20V8", "M6 8l3-4M18 8l-3-4", "M9 12h6"],
  },
};

export default hero;
